import React, { FormEvent, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import PagesPaths from '@/enums/PagesPaths';

import { Container, InputTextField } from '@/components';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  faBell,
  faInfoCircle,
  faKey,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuthContext, useToastNotification } from '@/hooks';
import { validateEmail, validatePhone } from '@/utils/formValidations';
import { UserData } from '@/types/auth';
import {
  TabPanel,
  TabPanelHeaderTemplateOptions,
  TabView,
} from 'primereact/tabview';
import styles from './UserSettings.module.scss';
import { ResetPassword } from './ResetPassword';
import Notifications from './Notifications/Notifications';
import { updateUserData } from './updateUser';

export interface UserSettingsFormState {
  user: string;
  fullName: string;
  email: string;
  phone: string;
}

const UserSettings: React.FC = () => {
  const { t } = useTranslation('profilePage');
  const { showToast } = useToastNotification();
  const navigate = useNavigate();
  const { userDataDecoded } = useAuthContext();
  const [formData, setFormData] = useState<UserSettingsFormState>({
    user: '',
    fullName: '',
    email: '',
    phone: '',
  });
  const [formErrors, setFormErrors] = useState({
    email: '',
    phone: '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: keyof UserSettingsFormState
  ) => {
    const { value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const tab1HeaderTemplate = (options: TabPanelHeaderTemplateOptions) => {
    return (
      <button
        className={styles.headerTemplate}
        onClick={options.onClick}
        type="submit"
      >
        <FontAwesomeIcon icon={faUser} className={styles.icon} />
        <h3>{t('personalData')}</h3>
      </button>
    );
  };

  const tab2HeaderTemplate = (options: TabPanelHeaderTemplateOptions) => {
    return (
      <button
        className={styles.headerTemplate}
        onClick={options.onClick}
        type="submit"
      >
        <FontAwesomeIcon icon={faKey} className={styles.icon} />
        <h3>{t('password')}</h3>
      </button>
    );
  };

  const tab3HeaderTemplate = (options: TabPanelHeaderTemplateOptions) => {
    return (
      <button
        className={styles.headerTemplate}
        onClick={options.onClick}
        type="submit"
      >
        <FontAwesomeIcon icon={faBell} className={styles.icon} />
        <h3>{t('notificationsScheduler')}</h3>
      </button>
    );
  };

  useEffect(() => {
    setFormData({
      user: userDataDecoded.username || '',
      fullName: userDataDecoded.name || '',
      email: userDataDecoded.email || '',
      phone: userDataDecoded.phone || '',
    });
  }, [userDataDecoded]);

  const validateForm = () => {
    const phoneError = validatePhone(formData.phone, {
      required: t('errors.phoneRequired'),
      invalid: t('errors.invalidPhone'),
      lengthExceeded: t('errors.phoneLengthExceeded'),
    });
    const emailError = validateEmail(formData.email, {
      required: t('errors.emailRequired'),
      invalid: t('errors.invalidEmail'),
    });

    setFormErrors({
      email: emailError,
      phone: phoneError,
    });

    const isFormValid = !phoneError && !emailError;

    return isFormValid;
  };

  const updateDataMutation = useMutation({
    mutationFn: updateUserData,
    onSuccess: () => {
      showToast({
        severity: 'success',
        detail: t('success.userData'),
      });
    },
    onError: () => {
      showToast({
        severity: 'error',
        detail: t('errors.toastText'),
      });
    },
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    const userData: UserData = {
      oidUsuario: 0,
      email: formData.email,
      telefono: formData.phone,
    };
    updateDataMutation.mutate(userData);
  };

  const submitButtonText = updateDataMutation.isPending
    ? t('buttonLoading')
    : t('buttonCreate');
  return (
    <Container title={t('config')} className={styles.userSettingsPage}>
      <TabView className={styles.noPadding}>
        <TabPanel
          className={styles.tabPanelHeader}
          header="fclImport"
          headerTemplate={tab1HeaderTemplate}
        >
          <div className={styles.container}>
            <form className={styles.userSettingsForm} onSubmit={handleSubmit}>
              <InputTextField
                id="user"
                name="user"
                value={formData.user}
                onChange={(e) => handleInputChange(e, 'user')}
                placeholder={t('user')}
                label={t('user')}
                readOnly
                disabled
              />

              <InputTextField
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={(e) => handleInputChange(e, 'fullName')}
                placeholder={t('fullName')}
                label={t('fullName')}
                readOnly
                disabled
              />

              <InputTextField
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => handleInputChange(e, 'email')}
                placeholder={t('email')}
                label={t('email')}
                error={formErrors.email}
              />
              <small>{t('emailNote')}</small>

              <InputTextField
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange(e, 'phone')}
                placeholder={t('phone')}
                label={t('phone')}
                error={formErrors.phone}
              />
              <Button
                disabled={
                  updateDataMutation.isPending ||
                  (formData.email === userDataDecoded.email &&
                    formData.phone === userDataDecoded.phone)
                }
                type="submit"
                className={styles.button}
              >
                {submitButtonText}
              </Button>
              <div className={styles.downloadPdf}>
                <div className={styles.textRow}>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className={styles.icon}
                  />

                  <div>
                    <p>{t('personalDataModificationNote')}</p>
                  </div>
                </div>
                <div className={styles.actionsRow}>
                  <Button
                    label=""
                    link
                    onClick={() => navigate(PagesPaths.Contact)}
                  >
                    <h5>{t('contactUs')}</h5>
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </TabPanel>
        <TabPanel
          className={styles.tabPanelHeader}
          header="fclExport"
          headerTemplate={tab2HeaderTemplate}
        >
          <div className={styles.container}>
            <ResetPassword isChangingPassword />
          </div>
        </TabPanel>
        <TabPanel
          className={styles.tabPanelHeader}
          header="notificationsScheduler"
          headerTemplate={tab3HeaderTemplate}
        >
          <Notifications />
        </TabPanel>
      </TabView>
    </Container>
  );
};

export default UserSettings;
